import { Component, Event, EventEmitter, h, JSX, Prop, State, Watch } from '@stencil/core';
import { DateUtils } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { TimelineBubble } from '../../global/models/Timeline';

@Component({
  tag: 'concourse-timeline',
  styleUrl: 'concourse-timeline.css',
  shadow: true,
})
export class ConcourseTimeline {
  @Prop() bubbles: TimelineBubble[] = [];
  @Prop() activeIndex: number = 0;
  /** ISO date string or Date — prefer ISO string so Stencil detects prop changes reliably */
  @Prop() dueDate: Date | string | null = null;
  @Prop() hideCreateBubble: boolean = false;

  @State() today: Date = DateUtils.currentDate;
  @State() barWidth: number = 0;
  @State() resolvedDueDate: Date | null = null;

  @Event({bubbles: false}) bubbleClick: EventEmitter<number[]>;
  @Event({bubbles: false}) barClick: EventEmitter<number>;
  @Event({bubbles: false}) dueDateClick: EventEmitter<number>;

  private timelineContainerEl: HTMLElement;
  private resizeObserver: ResizeObserver;

  componentWillLoad() {
    this.resolvedDueDate = this.parseDueDate(this.dueDate);
  }

  componentDidLoad() {
    this.setupResizeObserver();
  }

  @Watch('dueDate')
  onDueDateChange(newDueDate: Date | string | null) {
    this.resolvedDueDate = this.parseDueDate(newDueDate);
  }

  private parseDueDate(value: Date | string | null): Date | null {
    if (!value) return null;
    return value instanceof Date ? value : DateUtils.fromISO(value);
  }

  componentDidUpdate() {
    // Re-measure when bubbles/dueDate change without remounting (e.g. workflow switch)
    if (this.timelineContainerEl && this.barWidth === 0) {
      this.setupResizeObserver();
    }
  }

  private setupResizeObserver() {
    if (!this.timelineContainerEl) return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(entries => {
      const width = Math.round(entries[0]?.contentRect.width ?? 0);
      if (width !== this.barWidth) {
        this.barWidth = width;
      }
    });
    this.resizeObserver.observe(this.timelineContainerEl);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  roundTime(date: Date): number {
    return Math.round(date.getTime() / DateUtils.MILLIS_PER_HOUR) * DateUtils.MILLIS_PER_HOUR;
  }

  render(): JSX.Element {
    const canRender = this.barWidth > 0 && this.bubbles?.length > 0;
    let startTime = 0;
    let endTime = 0;
    let prevBarX = 0;
    let prevBubbleX = 0;
    let bubbleGroup: number[] = [];
    const bubbleHalfWidth = 17;
    const minBubbleGap = 5;

    if (canRender) {
      startTime = this.roundTime(this.bubbles[0].doneDate || this.bubbles[0].dueDate);
      const lastState = this.bubbles[this.bubbles.length - 1];
      const lastStateDate = lastState.doneDate ?? lastState.dueDate;
      endTime = this.resolvedDueDate ? Math.max(this.roundTime(lastStateDate), this.roundTime(this.resolvedDueDate)) : this.roundTime(lastStateDate);
    }

    return (
      <div class="timeline-container" ref={el => this.setTimelineContainerRef(el)}>
        {canRender && this.roundTime(this.today) >= startTime && this.roundTime(this.today) <= endTime && (
          <div class="timeline-marker" style={{ left: `${this.getDateXPos(startTime, endTime, this.today)}px` }}>
            <small class="rounded-bg">today {this.today.toISOString().substring(5, 10)}</small>
            <div class="vertical-bar" />
          </div>
        )}
        {canRender && this.resolvedDueDate && (
          <div
            class="timeline-marker"
            style={{ left: `${this.getDateXPos(startTime, endTime, this.resolvedDueDate)}px`, cursor: 'pointer' }}
            onClick={() => this.dueDateClick.emit(this.bubbles.length - 1)}
          >
            <small class="rounded-bg">due {this.resolvedDueDate.toISOString().substring(5, 10)}</small>
            <div class="vertical-bar" />
          </div>
        )}
        <div class="timeline-bar">
          {canRender &&
            this.bubbles.slice(1).map((state, index) => {
              const currentIdx = this.activeIndex - 1;
              const isCurrentSegment = index === currentIdx;
              const isPastDueWhilePending = !state.doneDate && state.dueDate && state.dueDate.getTime() < this.today.getTime();
              const dueDatePx = this.getDateXPos(startTime, endTime, state.dueDate);
              const doneDatePx = state.doneDate ? this.getDateXPos(startTime, endTime, state.doneDate) : null;
              const todayPx = this.getDateXPos(startTime, endTime, this.today);
              const barEndX = doneDatePx || (isCurrentSegment && isPastDueWhilePending ? todayPx : dueDatePx);
              const barStartX = prevBarX;
              prevBarX = barEndX;
              if (barEndX - barStartX < 10) {
                return <div />;
              }
              const segmentWidth = Math.min(barEndX - barStartX, this.barWidth - barStartX);
              const dueSegmentWidth = Math.min(Math.max(0, dueDatePx - barStartX), segmentWidth);
              const overdueWidth = doneDatePx && doneDatePx > dueDatePx
                ? Math.min(doneDatePx - dueDatePx, segmentWidth - dueSegmentWidth)
                : isCurrentSegment && isPastDueWhilePending
                ? Math.min(todayPx - dueDatePx, segmentWidth - dueSegmentWidth)
                : 0;
              return (
                <div
                  class={`bar ${!state.doneDate ? 'clickable' : ''}`}
                  style={{ left: `${barStartX}px`, width: `${segmentWidth}px` }}
                  onClick={() => (!state.doneDate ? this.barClick.emit(index) : console.log('Not clickable'))}
                >
                  <div class={index < currentIdx ? 'complete' : index == currentIdx ? 'current' : 'future'} style={{ width: `${dueSegmentWidth}px` }} />
                  {overdueWidth > 0 && <div class="overdue" style={{ left: `${dueSegmentWidth}px`, width: `${overdueWidth}px` }} />}
                </div>
              );
            })}
          {canRender && this.resolvedDueDate && (
            <div
              class="bar clickable"
              style={{ left: `${prevBarX}px`, width: `${Math.max(0, this.getDateXPos(startTime, endTime, this.resolvedDueDate) - prevBarX)}px` }}
              onClick={() => this.barClick.emit(this.bubbles.length - 1)}
            >
              <div class="future" style={{ width: `100%` }} />
            </div>
          )}
        </div>
        {canRender &&
          this.bubbles.map((state, index) => {
            const isCurrent = index === this.activeIndex;
            const isPastDueWhilePending = !state.doneDate && state.dueDate && state.dueDate.getTime() < this.today.getTime();
            const date = state.doneDate || (isCurrent && isPastDueWhilePending ? this.today : state.dueDate);
            let posX = this.getDateXPos(startTime, endTime, date);
            const next = index < this.bubbles.length - 1 ? this.bubbles[index + 1] : null;
            const closeToPrev = index > 0 && posX < prevBubbleX + minBubbleGap;
            if (closeToPrev) {
              bubbleGroup.push(index);
              const nudgedPos = prevBubbleX + minBubbleGap;
              // Spread overlapping bubbles only while there is room; otherwise keep date position and overlap
              if (nudgedPos <= this.barWidth - bubbleHalfWidth) {
                posX = nudgedPos;
              }
            } else {
              bubbleGroup = [index];
            }
            const nextPosX = next ? this.getDateXPos(startTime, endTime, next.doneDate || next.dueDate) : null;
            const closeToNext = nextPosX != null && nextPosX < posX + 50;
            prevBubbleX = posX;
            const isOverdue = (state.doneDate && state.doneDate.getTime() > state.dueDate.getTime()) || (isCurrent && isPastDueWhilePending);
            const bubbleClass = index > this.activeIndex ? 'future' : isOverdue ? 'overdue' : isCurrent ? 'current' : 'complete';
            const bubbleIcon = PeopleService.getGroupIcon(state.people);
            const bubbleGroupCopy = [...bubbleGroup]; // Required to avoid mutation of the array as the map iterates over it.
            const initials = state.people.length == 1 && !state.people[0].image?.data ? PeopleService.getInitials(state.people[0]) : '';
            if (this.hideCreateBubble && index == 0) return <div />;
            return (
              <div class="timeline-bubble" style={{ left: `${prevBubbleX}px` }} onClick={() => this.bubbleClick.emit(bubbleGroupCopy)}>
                <div class="circle-stack">
                  <div class={`circle ${bubbleClass} ${closeToPrev ? 'shadow-left' : ''}`} style={{ backgroundImage: `url(${bubbleIcon})` }}>
                    {initials}
                  </div>
                  {index < this.activeIndex && <ion-icon name="checkmark-circle" class="check-complete"></ion-icon>}
                </div>
                {(isCurrent || !closeToNext) && (
                  <div class={`label ${isCurrent ? '' : 'ion-hide-sm-down'}`}>
                    <div>{state.label}</div>
                    <small>{date.toISOString().substring(5, 10)}</small>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  }

  private setTimelineContainerRef(el: HTMLElement) {
    if (!el || el === this.timelineContainerEl) return;
    this.timelineContainerEl = el;
    this.setupResizeObserver();
  }

  getDateXPos(startTime: number, endTime: number, date: Date): number {
    if (endTime <= startTime || !this.barWidth) return 0;
    const pos = ((date.getTime() - startTime) / (endTime - startTime)) * this.barWidth;
    return Math.min(this.barWidth, Math.max(0, pos));
  }
}
